import React from 'react';
import { Globe, MessageCircle, ArrowRight, Check, Code } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import Navbar from '../Navbar';
import Footer from '../Footer';
import { useAuth } from '../../context/AuthContext';

const WebsitePage = () => {
  const { login } = useAuth();

  const features = [
    'Widget de chat no seu site',
    'Chatbot com IA integrado',
    'Captura de leads 24/7',
    'Personalização completa',
  ];

  const useCases = [
    { title: 'Suporte ao Cliente', desc: 'Atendimento automatizado 24/7' },
    { title: 'Geração de Leads', desc: 'Capture visitantes do site' },
    { title: 'FAQ Automático', desc: 'Responda perguntas frequentes' },
    { title: 'Agendamentos', desc: 'Marque reuniões automaticamente' },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="pt-28 pb-16">
        {/* Hero */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-indigo-100 text-indigo-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Globe className="h-4 w-4" />
                Website Widget
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Chatbot para seu <span className="text-[#3b82f6]">Website</span>
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                Adicione um widget de chat inteligente ao seu site e converta visitantes em clientes.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button onClick={login} size="lg" className="bg-[#3b82f6] hover:bg-[#2563eb]">
                  Começar gratuitamente
                </Button>
                <Button variant="outline" size="lg">
                  Ver demonstração
                </Button>
              </div>
            </div>
            <div className="bg-gray-100 rounded-3xl p-6">
              <div className="bg-white rounded-2xl shadow-xl p-4">
                <div className="flex items-center gap-2 mb-4 pb-3 border-b">
                  <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                  <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span className="ml-2 text-sm text-gray-500">seusite.com.br</span>
                </div>
                <div className="h-32 bg-gray-50 rounded-lg mb-4"></div>
                <div className="absolute bottom-8 right-8">
                  <div className="bg-[#3b82f6] text-white rounded-2xl p-4 shadow-lg max-w-xs">
                    <p className="text-sm">Olá! Como posso ajudar? 👋</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Features */}
        <div className="bg-gray-50 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
              Recursos do Widget
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {features.map((feature) => (
                <div key={feature} className="bg-white rounded-xl p-6 shadow-sm">
                  <Check className="h-6 w-6 text-green-500 mb-3" />
                  <p className="font-medium text-gray-900">{feature}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Use Cases */}
        <div className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
              Casos de uso populares
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {useCases.map((uc) => (
                <Card key={uc.title} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <h3 className="font-semibold text-gray-900 mb-2">{uc.title}</h3>
                    <p className="text-sm text-gray-600">{uc.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>

        {/* Code Snippet */}
        <div className="py-16 bg-gray-900">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8">
              <Code className="h-8 w-8 text-[#3b82f6] mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-white mb-2">Instalação Simples</h2>
              <p className="text-gray-400">Adicione uma linha de código ao seu site</p>
            </div>
            <div className="bg-gray-800 rounded-xl p-6 font-mono text-sm">
              <p className="text-gray-400">{'<!-- ChatAlfa Widget -->'}</p>
              <p className="text-green-400">{'<script src="https://cdn.chatalfa.com/widget.js"></script>'}</p>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <Card className="bg-gradient-to-br from-[#3b82f6] to-[#1d4ed8] text-white">
              <CardContent className="py-12 text-center">
                <h2 className="text-2xl font-bold mb-4">Pronto para adicionar chat ao seu site?</h2>
                <p className="text-blue-100 mb-6">Comece gratuitamente em minutos</p>
                <Button onClick={login} variant="secondary" size="lg" className="bg-white text-[#3b82f6] hover:bg-gray-100">
                  Começar agora <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default WebsitePage;
